import { useCallback, useState } from "react";
import type { ItemKind } from "../../../api/content/reviewDecoders";
import { LIBRARY_SEARCH_LIMIT, searchLibrary } from "../../../api/content/searchApi";
import { listSourceDocuments } from "../../../api/content/sourcesApi";
import { listMemories } from "../../../api/threads/memoriesApi";
import { useProjects } from "../../../projects/projectContext";
import { useLoad } from "../../../shared/loading/useLoad";
import { formatDocumentVersionRef } from "../../../shared/references/contentReferences";
import { useCorpusSelection } from "../../sources/corpus/corpusSelectionContext";
import { MAXIMUM_SUGGESTIONS_PER_KIND, suggestionKey, type Suggestion, type SuggestionQuery, type SuggestionTrigger } from "./suggestions";

export interface LoadedSuggestions {
  /** Library items the trigger offers, and after `@` the writer's own active memories. */
  readonly catalogue: readonly Suggestion[];
  /** Documents of the chosen corpus that the server matched to the `@` query. */
  readonly documents: readonly Suggestion[];
  /** A source could not be read; the others still suggest. */
  readonly someFailed: boolean;
}

type LibraryKind = Extract<ItemKind, Suggestion["kind"]>;
/** What each trigger reads from the library, constants so they key the reads. */
const LIBRARY_KINDS: Readonly<Record<SuggestionTrigger, readonly LibraryKind[]>> = {
  "@": ["artifact", "tool"],
  "/": ["skill", "prompt"],
};
const NOTHING = (): Promise<readonly Suggestion[]> => Promise.resolve([]);

/**
 * The project content the composer suggests (F21, F70), read lazily: nothing before the
 * first `@` or `/`. Then, once each while the composer is shown, the trigger's library
 * items by name through the library's one search (F38; the project's own and its pinned
 * libraries', the references Add… inserts) and, after `@`, the writer's personal memories
 * only (Johannes, 28 Oct: "memories are something for personal work only"). Per typed word,
 * the library search for it, so an item past the listing's first page is still found by its
 * name (Sol S3), and the chosen corpus's documents, one bounded page each. A changed query
 * aborts the previous reads (`useLoad`).
 */
export function useSuggestionSources(query: SuggestionQuery | null, projectId: string): LoadedSuggestions {
  const [wanted, setWanted] = useState<Readonly<Record<SuggestionTrigger, boolean>>>({ "@": false, "/": false });
  if (query !== null && !wanted[query.trigger]) {
    setWanted({ ...wanted, [query.trigger]: true });
  }

  const wantsContext = wanted["@"];
  const wantsInstructions = wanted["/"];
  const loadContext = useCallback(
    (signal: AbortSignal) => (wantsContext ? libraryItems(projectId, LIBRARY_KINDS["@"], signal) : NOTHING()),
    [wantsContext, projectId],
  );
  const loadInstructions = useCallback(
    (signal: AbortSignal) => (wantsInstructions ? libraryItems(projectId, LIBRARY_KINDS["/"], signal) : NOTHING()),
    [wantsInstructions, projectId],
  );
  // The search takes whole words; a word is searched once it is typed, the listing serves the rest.
  const searchedTrigger = query !== null && SEARCHABLE_WORD.test(query.query) ? query.trigger : null;
  const searchedWord = searchedTrigger === null ? null : (query?.query ?? null);
  const loadSearched = useCallback(
    (signal: AbortSignal) =>
      searchedTrigger === null || searchedWord === null ? NOTHING() : libraryItems(projectId, LIBRARY_KINDS[searchedTrigger], signal, searchedWord),
    [searchedTrigger, searchedWord, projectId],
  );
  const loadMemories = useCallback(
    (signal: AbortSignal) =>
      wantsContext
        ? listMemories(projectId, "active", signal).then((memories) =>
            memories.map((memory): Suggestion => ({ kind: "memory", reference: memory.memoryId, name: memory.title })),
          )
        : NOTHING(),
    [wantsContext, projectId],
  );

  const { selectedProjectId } = useProjects();
  const { selectedCorpus } = useCorpusSelection();
  // The chosen corpus belongs to the chosen project; a Thread of another project has none.
  const corpusVersion = selectedProjectId === projectId ? (selectedCorpus?.latestVersion ?? null) : null;
  const collectionId = corpusVersion?.collectionId ?? null;
  const commit = corpusVersion?.commit ?? null;
  const documentQuery = query?.trigger === "@" ? query.query : null;
  const loadDocuments = useCallback(
    (signal: AbortSignal) =>
      documentQuery === null || collectionId === null || commit === null
        ? NOTHING()
        : listSourceDocuments(
            { corpusVersion: { collectionId, commit }, query: documentQuery === "" ? undefined : documentQuery, offset: 0, limit: MAXIMUM_SUGGESTIONS_PER_KIND },
            signal,
          ).then((page) => page.documents.map((document): Suggestion => ({ kind: "document", reference: formatDocumentVersionRef(document.ref), name: document.key }))),
    [documentQuery, collectionId, commit],
  );

  const context = useLoad(loadContext).state;
  const instructions = useLoad(loadInstructions).state;
  const searched = useLoad(loadSearched).state;
  const memories = useLoad(loadMemories).state;
  const documents = useLoad(loadDocuments).state;
  const loaded = (state: typeof context) => (state.status === "loaded" ? state.value : []);
  return {
    catalogue: distinct([...loaded(context), ...loaded(instructions), ...loaded(searched), ...loaded(memories)]),
    documents: loaded(documents),
    someFailed: [context, instructions, searched, memories, documents].some((state) => state.status === "failed"),
  };
}

/** A typed word worth searching: letters and digits, joined as item names join them (`annotate-segments`, `list_witnesses`). */
const SEARCHABLE_WORD = /^[\p{L}\p{N}][\p{L}\p{N}_-]+$/u;

/** The library's items of `kinds` that `word` finds, or by name without one: what the composer matches as the writer types. */
function libraryItems(projectId: string, kinds: readonly LibraryKind[], signal: AbortSignal, word = ""): Promise<readonly Suggestion[]> {
  return searchLibrary(projectId, { query: word, kinds, contentTypes: [], limit: LIBRARY_SEARCH_LIMIT }, signal).then((items) =>
    items.flatMap((item): Suggestion[] => {
      const kind = kinds.find((asked) => asked === item.kind);
      return kind === undefined ? [] : [{ kind, reference: item.reference, name: item.name }];
    }),
  );
}

/** Each suggestion once: the listing and a word's search may both hold an item. */
function distinct(suggestions: readonly Suggestion[]): Suggestion[] {
  const seen = new Set<string>();
  return suggestions.filter((suggestion) => {
    const key = suggestionKey(suggestion);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
